import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sun, Moon, ArrowUpRight } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { NAV } from "../lib/data";

export default function Navbar() {
  const { theme, toggle } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "glass border-b border-line py-3" : "py-5 border-b border-transparent"
      }`}
    >
      <div className="max-w-[90rem] mx-auto px-6 lg:px-12 flex items-center justify-between">
        <Link to="/" data-testid="logo" className="flex items-center gap-2.5 group">
          <span className="relative grid place-items-center h-9 w-9 rounded-lg bg-gradient-to-br from-brand to-secondary glow-brand">
            <span className="font-heading font-bold text-[#05050A] text-lg">V</span>
          </span>
          <span className="font-heading text-lg font-bold tracking-tight">
            Vyn<span className="text-gradient">Five</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((item) => (
            <a
              key={item.label}
              href={item.href}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className="relative text-sm text-textsub hover:text-textmain transition-colors duration-300 group"
            >
              {item.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-brand transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="theme-toggle"
            aria-label="Toggle theme"
            className="grid place-items-center h-10 w-10 rounded-full glass hover:text-brand transition-colors"
          >
            {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
          </button>
          <a
            href="#contact"
            data-testid="nav-cta"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-textmain text-bg text-sm font-semibold px-5 py-2.5 hover:gap-2.5 transition-all duration-300"
          >
            Book Consultation <ArrowUpRight size={15} />
          </a>
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden grid place-items-center h-10 w-10 rounded-full glass"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden glass border-t border-line mt-3"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  className="text-textsub hover:text-brand text-lg font-heading"
                >
                  {item.label}
                </a>
              ))}
              <a href="#contact" onClick={() => setOpen(false)} className="mt-2 rounded-full bg-brand text-[#05050A] text-center font-semibold px-5 py-3">
                Book Consultation
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
